// Post a ride / Ask for a ride — same pieces as RideFormKit.swift on iOS:
// route tiles with common places, day chips ("Pick" + the next 6 days), a
// time, a seat stepper, contribution chips and a summary bar with the button.
// Desktop adds a live preview of how the post will look, plus a tip.
//
// Saved like iOS: `date` is the true UTC instant (the form's time is Mountain
// Time), `time` is "h:mm AM", `seats` is a number, `postedAt` the server time.

import React, { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAuth } from 'firebase/auth';
import { getDatabase, ref, push, serverTimestamp } from 'firebase/database';
import { COMMON, displayPlace, localDay, mountainToUtc } from '../places';
import './PostForm.css';

const MT = 'America/Denver';
const weekday = new Intl.DateTimeFormat('en-US', { timeZone: MT, weekday: 'short' });
const monthDay = new Intl.DateTimeFormat('en-US', { timeZone: MT, month: 'short', day: 'numeric' });
const longDay = new Intl.DateTimeFormat('en-US', { timeZone: MT, weekday: 'long', month: 'long', day: 'numeric' });
const QUICK = COMMON.slice(0, 6);
const CHIP_LABEL = { 'Denver Airport (DEN)': 'DEN Airport' };
const CONTRIBUTIONS = ['None', 'Gas', 'Food', '$5', '$10', '$15', '$20'];

/** "14:30" → "2:30 PM" */
function to12h(hhmm) {
  const [h, m] = hhmm.split(':').map(Number);
  return `${h % 12 || 12}:${String(m).padStart(2, '0')} ${h >= 12 ? 'PM' : 'AM'}`;
}

function nextDays(n) {
  return Array.from({ length: n }, (_, i) => {
    const d = new Date(Date.now() + i * 86400000);
    return { ymd: localDay(d), top: i === 0 ? 'Today' : weekday.format(d), bottom: monthDay.format(d) };
  });
}

function PlaceTile({ label, value, onChange, placeholder, autoFocus }) {
  return (
    <div className="pf-tile">
      <label className="pf-tile-field">
        <span className="pf-tile-label">{label}</span>
        <input value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder}
          maxLength={100} autoFocus={autoFocus} />
      </label>
      <div className="pf-chips" role="group" aria-label={`Common places for ${label.toLowerCase()}`}>
        {QUICK.map(p => (
          <button key={p} type="button" className={`pf-chip${value === p ? ' on' : ''}`} aria-pressed={value === p}
            onClick={() => onChange(p)}>{CHIP_LABEL[p] || p}</button>
        ))}
      </div>
    </div>
  );
}

export default function PostForm({ kind }) {
  const isRide = kind === 'ride';
  const navigate = useNavigate();
  const days = useMemo(() => nextDays(6), []);

  const [from, setFrom]       = useState('');
  const [to, setTo]           = useState('');
  // Noon today, or noon tomorrow once noon has passed.
  const [day, setDay]         = useState(() =>
    mountainToUtc(days[0].ymd, 12, 0).getTime() > Date.now() ? days[0].ymd : days[1].ymd);
  const [picking, setPicking] = useState(false);
  const [time, setTime]       = useState('12:00');
  const [seats, setSeats]     = useState(isRide ? 3 : 1);
  const [contrib, setContrib] = useState('None');
  const [custom, setCustom]   = useState(false);
  const [notes, setNotes]     = useState('');
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState('');

  const [hh, mm] = time.split(':').map(Number);
  const when = day && time ? mountainToUtc(day, hh, mm) : null;
  const inPast = when && when.getTime() < Date.now();
  const ready = from.trim() && to.trim() && when && !inPast;
  const daysAhead = when ? Math.floor((when - Date.now()) / 86400000) : 0;

  const summaryTitle = from.trim() && to.trim()
    ? `${displayPlace(from)} → ${displayPlace(to)}` : isRide ? 'Where are you driving?' : 'Where do you need to go?';
  const summarySub = when
    ? `${longDay.format(when)} · ${to12h(time)} · ${seats} seat${seats !== 1 ? 's' : ''}${isRide ? ' open' : ' needed'}`
    : 'Pick a day and time';

  const submit = async (e) => {
    e?.preventDefault();
    if (!ready) {
      setError(inPast ? 'That time has already passed.' : 'Add where from, where to, and when.');
      return;
    }
    const uid = getAuth().currentUser?.uid;
    if (!uid) { setError('You need to be logged in.'); return; }
    setSaving(true); setError('');
    try {
      const posted = await push(ref(getDatabase(), isRide ? 'rideReserve' : 'rideRequest'), {
        userID: uid,
        fromLocation: from.trim(),
        toLocation: to.trim(),
        seats,
        date: when.toISOString(),
        time: to12h(time),
        donationRequested: contrib === 'None' ? '' : contrib.trim().slice(0, 50),
        additionalInfo: notes.trim().slice(0, 300),
        postedAt: serverTimestamp(),
      });
      // Profile shows who needs this ride (or which rides match the request).
      navigate('/profile', { state: { justPosted: { kind, id: posted.key } } });
    } catch (err) {
      setError(`Couldn't post: ${err.message}`);
      setSaving(false);
    }
  };

  const swap = () => { setFrom(to); setTo(from); };

  return (
    <div className={`pf-page ${isRide ? 'ride' : 'request'}`}>
      <div className="pf-top">
        <button type="button" className="pf-back" onClick={() => navigate(-1)}>← Back</button>
      </div>

      <div className="pf-layout">
        <form className="pf-form" onSubmit={submit} noValidate>
          <h1>{isRide ? 'Post a ride' : 'Ask for a ride'}</h1>
          <p className="pf-lead">{isRide
            ? 'Riders going your way will find it, and anyone who asked for this route hears about it.'
            : 'Drivers going your way will see it, and you hear about any ride that matches.'}</p>

          <section className="pf-section">
            <div className="pf-route">
              <PlaceTile label="From" value={from} onChange={setFrom} placeholder="e.g. USAFA" autoFocus />
              <button type="button" className="pf-swap" onClick={swap} aria-label="Swap from and to" title="Swap">⇅</button>
              <PlaceTile label="To" value={to} onChange={setTo} placeholder="e.g. DEN Airport" />
            </div>
          </section>

          <section className="pf-section">
            <h2 className="pf-label">When</h2>
            <div className="pf-days" role="radiogroup" aria-label="Day">
              <button type="button" role="radio" aria-checked={picking} aria-label="Pick a date"
                className={`pf-day${picking ? ' on' : ''}`} onClick={() => setPicking(true)}>
                <strong aria-hidden="true">📅</strong><span>Pick</span>
              </button>
              {days.map(d => {
                const on = !picking && day === d.ymd;
                return (
                  <button key={d.ymd} type="button" role="radio" aria-checked={on} aria-label={`${d.top}, ${d.bottom}`}
                    className={`pf-day${on ? ' on' : ''}`} onClick={() => { setDay(d.ymd); setPicking(false); }}>
                    <strong>{d.top}</strong><span>{d.bottom}</span>
                  </button>
                );
              })}
            </div>
            <div className="pf-when-row">
              {picking && (
                <label className="pf-field">
                  <span>Date</span>
                  <input type="date" value={day} min={days[0].ymd} onChange={e => setDay(e.target.value)} />
                </label>
              )}
              <label className="pf-field">
                <span>{isRide ? 'Leaving at' : 'Want to leave at'}</span>
                <input type="time" value={time} step={300} onChange={e => setTime(e.target.value)} />
              </label>
            </div>
            {inPast && <p className="pf-warn">That time has already passed today.</p>}
          </section>

          <section className="pf-section pf-seats">
            <h2 className="pf-label">{isRide ? 'Open seats' : 'Seats needed'}</h2>
            <div className="pf-stepper" role="group" aria-label="Seats">
              <button type="button" onClick={() => setSeats(n => Math.max(1, n - 1))} disabled={seats <= 1} aria-label="One fewer seat">−</button>
              <output aria-live="polite">{seats}</output>
              <button type="button" className="plus" onClick={() => setSeats(n => Math.min(8, n + 1))} disabled={seats >= 8} aria-label="One more seat">+</button>
            </div>
          </section>

          <section className="pf-section">
            <h2 className="pf-label">{isRide ? 'Ask riders for' : "You'll chip in"}</h2>
            <div className="pf-chips wide">
              {CONTRIBUTIONS.map(c => (
                <button key={c} type="button" className={`pf-chip${!custom && contrib === c ? ' on' : ''}`}
                  onClick={() => { setCustom(false); setContrib(c); }}>{c === 'None' ? 'Nothing' : c}</button>
              ))}
              <button type="button" className={`pf-chip${custom ? ' on' : ''}`}
                onClick={() => { setCustom(true); if (CONTRIBUTIONS.includes(contrib)) setContrib(''); }}>Other</button>
            </div>
            {custom && (
              <input className="pf-input" value={contrib} onChange={e => setContrib(e.target.value)}
                maxLength={50} placeholder="Amount or item" autoFocus />
            )}
          </section>

          <section className="pf-section">
            <h2 className="pf-label">Notes <span>(optional)</span></h2>
            <textarea className="pf-input" rows={3} maxLength={300} value={notes} onChange={e => setNotes(e.target.value)}
              placeholder={isRide ? 'Pickup spot, luggage room, stops on the way…' : 'Flight time, luggage, flexibility…'} />
          </section>

          {error && <p className="pf-error" role="alert">{error}</p>}

          {/* Sticky on phones, inline on desktop */}
          <div className="pf-bar">
            <div className="pf-bar-text">
              <strong>{summaryTitle}</strong>
              <span>{summarySub}</span>
            </div>
            <button type="submit" className="pf-submit" disabled={saving || !ready}>
              {saving ? 'Posting…' : isRide ? 'Post ride' : 'Post request'}
            </button>
          </div>
        </form>

        {/* Desktop: live preview + a tip from real ride data */}
        <aside className="pf-aside" aria-label="Preview">
          <div className="pf-preview-label">Preview</div>
          <div className="pf-preview">
            <div className="pf-preview-time">{when ? to12h(time) : '—'}</div>
            <div className="pf-preview-route">
              {from.trim() ? displayPlace(from) : 'From'} <span>→</span> {to.trim() ? displayPlace(to) : 'To'}
            </div>
            <div className="pf-preview-day">{when ? longDay.format(when) : 'Pick a day'}</div>
            <div className="pf-preview-meta">
              <span className="pf-preview-seats">{seats} seat{seats !== 1 ? 's' : ''} {isRide ? 'left' : 'needed'}</span>
              {contrib && contrib !== 'None' && <span>{contrib}</span>}
            </div>
            {notes.trim() && <p className="pf-preview-note">{notes.trim()}</p>}
          </div>
          <div className="pf-tip">
            <strong>{isRide ? 'Post early' : 'Ask early'}</strong>
            <span>{isRide
              ? (daysAhead >= 7 ? 'Nice. Rides posted a week or more ahead get booked 83% of the time.'
                : 'Rides posted a week or more ahead get booked 83% of the time.')
              : 'Drivers post break rides weeks ahead. The sooner you ask, the more of them see it.'}</span>
          </div>
        </aside>
      </div>
    </div>
  );
}
